import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { ArchiveBoxArrowDownIcon } from '@heroicons/react/24/outline';
import { PlanItem } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';

interface SaveLibraryModalProps {
  isOpen: boolean;
  // New custom items about to be materialized (label chosen, no content yet).
  items: PlanItem[];
  isSaving: boolean;
  confirmText: string;
  onCancel(): void;
  onConfirm(): void;
}

// Pre-save heads-up that this plan's new custom items will also be created as
// draft contents in their labeled libraries. Purely informational — confirming
// saves the plan and creates the contents; Cancel aborts the save.
export default function SaveLibraryModal({
  isOpen,
  items,
  isSaving,
  confirmText,
  onCancel,
  onConfirm,
}: SaveLibraryModalProps) {
  return (
    <DialogRoot open={isOpen} onOpenChange={onCancel}>
      <DialogContent>
        <DialogHeader>
          <Flex align='center' gap={2}>
            <Icon as={ArchiveBoxArrowDownIcon} w={5} h={5} color='brand.solid' />
            <DialogTitle>New content will be saved to your library</DialogTitle>
          </Flex>
        </DialogHeader>

        <DialogBody>
          <Text fontSize='sm' color='fg.muted' mb={4}>
            When this plan saves, the items below will be added to your content library as drafts.
            You can edit them later to add images or more details, and reuse them when building
            future plans.
          </Text>

          <Text textStyle='eyebrow' mb={2}>
            {items.length} new content item{items.length === 1 ? '' : 's'} added to this plan
          </Text>
          <Box bg='bg.subtle' borderRadius='md' px={4} py={2}>
            {items.map((item) => {
              const slot = slotFor(String(item.content_type_id));
              return (
                <Flex key={item.uid} align='center' justify='space-between' gap={3} py={1.5}>
                  <Text fontSize='sm' fontWeight='600' color='fg.strong' lineClamp={1}>
                    {item.name}
                  </Text>
                  {item.content_type_name && (
                    <Text
                      as='span'
                      fontSize='xs'
                      fontWeight='semibold'
                      px={2}
                      py={0.5}
                      borderRadius='full'
                      bg={`slot${slot}.bg`}
                      color={`slot${slot}.fg`}
                      flexShrink={0}
                    >
                      {item.content_type_name}
                    </Text>
                  )}
                </Flex>
              );
            })}
          </Box>
        </DialogBody>

        <DialogFooter>
          <Button variant='ghost' onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button colorPalette='brand' loading={isSaving} onClick={onConfirm}>
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
